import { toBlob } from 'html-to-image';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FORMATIONS } from '../data/positions.ts';
import { posterFontCss } from '../lib/fontEmbed.ts';
import { useSquad } from '../lib/squadContext.ts';
import { POSTER } from '../lib/theme.ts';
import type { Player } from '../types.ts';

/** Width of one poster column, as a percentage of the pitch box. */
const COLUMN_WIDTH = 12.5;

/** Pixel density of the exported PNG relative to the on-screen poster. */
const EXPORT_SCALE = 2;

/** Reserved for the header controls, mirrored on the left so the title stays centred. */
const CONTROLS_WIDTH = 210;

/** Printed on a starter plate that nobody has been assigned to. */
const VACANT_LABEL = '- YOK -';

/**
 * How far the pitch is tilted away from the reader, and from how far off it is viewed.
 * Together these set how much narrower the far touchline is: with the near edge as the
 * hinge, the far one comes out at roughly `perspective / (perspective + height·sin θ)`
 * of full width — about 85% at the poster's usual height. Only the drawing tilts; the
 * name plates stay on their own flat layer so the type never skews.
 */
const PITCH_TILT_DEG = 14;
const PITCH_PERSPECTIVE = 1255;

interface PosterCell {
  left: string;
  top: string;
  /** Name of the player holding the slot, or `VACANT_LABEL` when it is empty. */
  starter: string;
  /** True when the slot is unfilled, so the plate can read as a gap and not a name. */
  vacant: boolean;
  reserves: Player[];
}

/** Full-screen printed team sheet: the whole first-team list on one pitch. */
export default function SquadPoster() {
  const { state, dispatch, players, firstTeam, board, posterName } = useSquad();
  const overlayRef = useRef<HTMLDivElement>(null);
  /** True while the PNG is being rasterised, which is also when the controls hide. */
  const [exporting, setExporting] = useState(false);

  const { cells, overflow } = useMemo(() => {
    const slots = FORMATIONS[state.formation];
    const startingIds = Object.values(state.lineup);

    // The very same arrangement the pitch and the depth chart show. The poster only
    // differs in how deep it prints: the starter on gold, then everyone else queued
    // behind him for that slot.
    const cover = board.queues.map((queue) =>
      queue.filter((p) => startingIds.indexOf(p.id) < 0),
    );

    // Horizontally the formation's distinct lines become evenly spaced columns, so the
    // poster reads left to right from the keeper. That rotates the pitch 90° relative
    // to the tactics board: the slot's top % picks the column, its left % the row.
    const columnValues = slots
      .map((slot) => 100 - slot[2])
      .filter((v, i, a) => a.indexOf(v) === i)
      .sort((a, b) => a - b);
    const columnCount = columnValues.length;
    // Column centres start one half-plate inside the edges, so nothing overlaps.
    const firstCentre = 3 + COLUMN_WIDTH / 2;
    const lastCentre = 97 - COLUMN_WIDTH / 2;

    const rendered: PosterCell[] = slots.map((slot, i) => {
      const id = state.lineup[i];
      const p = id ? (players.find((o) => o.id === id) ?? null) : null;
      const column = columnValues.indexOf(100 - slot[2]);
      const left =
        columnCount > 1
          ? firstCentre + column * ((lastCentre - firstCentre) / (columnCount - 1))
          : 50;
      return {
        left: left + '%',
        top: Math.max(9, Math.min(91, 50 + (slot[1] - 50) * 1.3)) + '%',
        starter: p ? posterName(p) : VACANT_LABEL,
        vacant: !p,
        reserves: cover[i],
      };
    });

    return {
      cells: rendered,
      overflow: board.overflow.filter((p) => startingIds.indexOf(p.id) < 0),
    };
  }, [state.formation, state.lineup, players, board, posterName]);

  // The capture runs after a repaint so the hidden controls are already gone from the
  // DOM the rasteriser clones; otherwise they would show up in the PNG.
  useEffect(() => {
    if (!exporting) return;
    let cancelled = false;

    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(async () => {
        const node = overlayRef.current;
        if (!node || cancelled) return;
        try {
          // A blob rather than a data URL: the poster scales with the viewport, and on a
          // large display the base64 string gets big enough for browsers to refuse it.
          const blob = await toBlob(node, {
            pixelRatio: EXPORT_SCALE,
            cacheBust: true,
            // The overlay paints its own gradient, so this only backs the edges.
            backgroundColor: POSTER.ink,
            // Supplied ourselves; letting html-to-image collect it trips a
            // SecurityError on the cross-origin Google Fonts stylesheet.
            fontEmbedCSS: await posterFontCss(),
          });
          if (cancelled || !blob) return;

          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.download = `galatasaray-2026-2027-genis-kadro-${state.formation}.png`;
          link.href = url;
          document.body.appendChild(link);
          link.click();
          link.remove();
          URL.revokeObjectURL(url);
        } catch {
          // Export failed (usually a font that would taint the canvas). The controls
          // come back either way so the poster never gets stuck without them.
        } finally {
          if (!cancelled) setExporting(false);
        }
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [exporting, state.formation]);

  if (!state.posterOpen) return null;

  return (
    <div
      ref={overlayRef}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 90,
        background:
          'radial-gradient(120% 90% at 50% 40%, #a41a1f 0%, #8d1218 55%, #6f0d12 100%)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          padding: '22px 26px 10px',
        }}
      >
        <div style={{ width: CONTROLS_WIDTH }}></div>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div
            style={{
              fontSize: 'clamp(20px, 2.4vw, 38px)',
              fontWeight: 800,
              letterSpacing: '0.01em',
              color: POSTER.gold,
              lineHeight: 1,
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            GALATASARAY 2026/2027 GENİŞ KADRO
          </div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.2em',
              color: 'rgba(255,255,255,0.55)',
              marginTop: 8,
            }}
          >
            {state.formation + ' · ' + firstTeam.length + ' oyuncu'}
          </div>
        </div>
        <div
          style={{
            width: CONTROLS_WIDTH,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            // Kept in the layout rather than unmounted, so hiding them for the capture
            // cannot nudge the title off centre.
            visibility: exporting ? 'hidden' : 'visible',
          }}
        >
          <button
            onClick={() => setExporting(true)}
            disabled={exporting}
            title="Önizlemeyi PNG olarak indir"
            style={{
              border: '2px solid ' + POSTER.gold,
              background: POSTER.gold,
              color: POSTER.ink,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '0.1em',
              padding: '7px 12px',
              cursor: exporting ? 'progress' : 'pointer',
            }}
          >
            KAYDET
          </button>
          <button
            onClick={() => dispatch({ type: 'setPoster', open: false })}
            style={{
              border: '2px solid ' + POSTER.gold,
              background: 'transparent',
              color: POSTER.gold,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '0.1em',
              padding: '7px 12px',
              cursor: 'pointer',
            }}
          >
            KAPAT ✕
          </button>
        </div>
      </div>

      <div
        style={{
          flex: 1,
          position: 'relative',
          margin: '6px 26px 20px',
          // Only the pitch layer below is tilted; the name plates stay square so the
          // type never skews. The viewing distance lives here, on the parent.
          perspective: PITCH_PERSPECTIVE,
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            border: '2px solid rgba(255,255,255,0.10)',
            // Hinged on the touchline nearest the reader, so the far side recedes and
            // the pitch reads as ground rather than a flat rectangle. scaleY gives back
            // the height the tilt foreshortens, keeping the plates over the grass.
            transform: `rotateX(${PITCH_TILT_DEG}deg) scaleY(1.05)`,
            transformOrigin: 'center bottom',
          }}
        >
        <svg
          viewBox="0 0 105 68"
          preserveAspectRatio="none"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
          fill="none"
          stroke="rgba(255,255,255,0.10)"
          strokeWidth="0.45"
        >
          <line x1="52.5" y1="0" x2="52.5" y2="68"></line>
          <ellipse cx="52.5" cy="34" rx="9.15" ry="9.15"></ellipse>
          <circle cx="52.5" cy="34" r="0.6" fill="rgba(255,255,255,0.16)" stroke="none"></circle>
          <rect x="0" y="13.84" width="16.5" height="40.32"></rect>
          <rect x="0" y="24.84" width="5.5" height="18.32"></rect>
          <circle cx="11" cy="34" r="0.6" fill="rgba(255,255,255,0.16)" stroke="none"></circle>
          {/* Penalty arc: centred on the spot, so it bulges away from the box. */}
          <path d="M 16.5 26.7 A 9.15 9.15 0 0 1 16.5 41.3"></path>
          <rect x="88.5" y="13.84" width="16.5" height="40.32"></rect>
          <rect x="99.5" y="24.84" width="5.5" height="18.32"></rect>
          <circle cx="94" cy="34" r="0.6" fill="rgba(255,255,255,0.16)" stroke="none"></circle>
          <path d="M 88.5 26.7 A 9.15 9.15 0 0 0 88.5 41.3"></path>
          {/* Corner arcs — 1 m radius at this box's one-unit-per-metre scale, drawn
              thinner so the stroke does not swallow so small an arc. */}
          <path d="M 1 0 A 1 1 0 0 1 0 1" strokeWidth="0.22"></path>
          <path d="M 104 0 A 1 1 0 0 0 105 1" strokeWidth="0.22"></path>
          <path d="M 0 67 A 1 1 0 0 1 1 68" strokeWidth="0.22"></path>
          <path d="M 104 68 A 1 1 0 0 1 105 67" strokeWidth="0.22"></path>
        </svg>
        </div>

        {cells.map((cell, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: cell.left,
              top: cell.top,
              transform: 'translate(-50%,-50%)',
              transformStyle: 'preserve-3d',
              width: '12.5%',
              minWidth: 96,
              maxWidth: 230,
              textAlign: 'center',
            }}
          >
            <div
              style={{
                background: POSTER.gold,
                color: POSTER.ink,
                fontFamily: "'PT Sans Narrow', 'Archivo', sans-serif",
                fontSize: 'clamp(16px, 1.7vw, 30px)',
                // A gap is set bold and a touch wider so it reads as a missing slot
                // rather than as a player's name.
                fontWeight: cell.vacant ? 700 : 400,
                letterSpacing: cell.vacant ? '0.03em' : '0.02em',
                lineHeight: 1.1,
                padding: '1px 4px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                boxShadow: '2px 3px 0 ' + POSTER.shadow,
                transform: 'skewY(-2.2deg)',
              }}
            >
              {cell.starter}
            </div>
            {cell.reserves.map((p) => (
              <div
                key={p.id}
                style={{
                  background: POSTER.paper,
                  color: POSTER.ink,
                  fontFamily: "'PT Sans Narrow', 'Archivo', sans-serif",
                  fontSize: 'clamp(15px, 1.6vw, 28px)',
                  fontWeight: 400,
                  letterSpacing: '0.02em',
                  lineHeight: 1.1,
                  padding: '1px 4px',
                  marginTop: 5,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  boxShadow: '2px 3px 0 ' + POSTER.shadowSoft,
                  transform: 'skewY(-2.2deg)',
                }}
              >
                {posterName(p)}
              </div>
            ))}
          </div>
        ))}
      </div>

      {overflow.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            padding: '0 24px 16px',
          }}
        >
          <span
            style={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: '0.14em',
              color: 'rgba(255,255,255,0.55)',
            }}
          >
            DİZİLİŞE SIĞMAYANLAR
          </span>
          {overflow.map((p) => (
            <span
              key={p.id}
              style={{
                background: POSTER.paper,
                color: POSTER.ink,
                fontSize: 13,
                fontWeight: 700,
                padding: '3px 8px',
                transform: 'skewY(-2.2deg)',
                boxShadow: '2px 3px 0 ' + POSTER.shadowSoft,
              }}
            >
              {posterName(p)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
